import { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../utils/api';

export function useReportStudents({ schoolId, classId, sectionId, academicYearId, status, enabled = true, allowAllClasses = false, includeFee = false }) {
  const [students, setStudents] = useState([]);
  const [classIsTwelve, setClassIsTwelve] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchStudents = useCallback(async () => {
    if (!enabled || !schoolId) {
      setStudents([]);
      return;
    }
    if (!classId && !allowAllClasses) {
      setStudents([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = { school_id: schoolId };
      if (classId) params.class_id = classId;
      if (sectionId) params.section_id = sectionId;
      if (academicYearId) params.academic_year_id = academicYearId;
      if (status && status !== 'all') params.status = status;
      if (includeFee) params.include_fee = '1';
      const res = await api.get('/reports/students', { params });
      const data = res.data?.data || {};
      setStudents(data.students || []);
      setClassIsTwelve(Boolean(data.class_is_twelve));
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load students');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [schoolId, classId, sectionId, academicYearId, status, enabled, allowAllClasses, includeFee]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  return { students, classIsTwelve, loading, error, refetch: fetchStudents };
}

export function filterStudentsBySearch(students, search) {
  const q = String(search || '').trim().toLowerCase();
  if (!q) return students;
  return students.filter((s) => {
    const name = `${s.first_name || ''} ${s.last_name || ''}`.toLowerCase();
    return (
      name.includes(q)
      || String(s.admission_no || '').toLowerCase().includes(q)
      || String(s.roll_no || '').toLowerCase().includes(q)
    );
  });
}

export function useStudentSelection(filteredStudents) {
  const [selectedIds, setSelectedIds] = useState([]);

  const toggleOne = useCallback((id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const toggleAll = useCallback((checked, ids) => {
    setSelectedIds(checked ? [...ids] : []);
  }, []);

  const clearSelection = useCallback(() => setSelectedIds([]), []);

  const allSelected = useMemo(() => {
    if (!filteredStudents.length) return false;
    return filteredStudents.every((s) => selectedIds.includes(s.id));
  }, [filteredStudents, selectedIds]);

  return { selectedIds, setSelectedIds, toggleOne, toggleAll, clearSelection, allSelected };
}
